import { useMemo } from 'react';
import { toaster } from '@optiaxiom/react';
import { Query, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { HYPATIA_URL } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import {
  WorkflowAgent,
  WorkflowAgentExecution,
  WorkflowAgentTriggerResponse,
} from '@frontend/types/workflow-agent';

import { useWorkflowToGraph } from '../components/workflow-agent/hooks/useWorkflowToGraph';
import {
  createWorkflowAgent,
  deleteWorkflowAgent,
  getWorkflowAgent,
  getWorkflowAgentExecution,
  getWorkflowAgents,
  triggerWorkflowAgent,
  updateWorkflowAgent,
} from '../services/workflow-agent';

export function useGetWorkflowAgentsList() {
  const { instanceId } = useInstanceId();
  const {
    data: agents,
    isPending,
    error,
  } = useQuery<WorkflowAgent[]>({
    queryKey: getWorkflowAgents(instanceId ?? '').key(),
    queryFn: getWorkflowAgents(instanceId ?? '').queryFn(),
    enabled: !!instanceId,
  });

  return { agents: agents ?? [], isPending, error };
}

export function useGetWorkflowAgent(url: string, enabled: boolean) {
  const {
    data: agent,
    isPending,
    error,
  } = useQuery<WorkflowAgent>({
    queryKey: ['opal:workflow-agent', url],
    queryFn: getWorkflowAgent(url),
    enabled: !!url && enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return { agent: agent ?? undefined, isPending, error };
}

export function useGetWorkflowAgentWithGraph(url: string, enabled: boolean) {
  const { agent, isPending, error } = useGetWorkflowAgent(url, enabled);

  // Extract and validate metadata from agent_metadata field
  const metadata = useMemo(() => {
    if (!agent?.agent_metadata || typeof agent.agent_metadata !== 'object') {
      return { nodes: [] };
    }

    // Type guard to check if metadata nodes are valid
    const isValidMetadataNode = (
      node: unknown
    ): node is {
      id: string;
      position?: { x: number; y: number };
      measured?: { width: number; height: number };
    } => {
      return (
        typeof node === 'object' &&
        node !== null &&
        'id' in node &&
        typeof (node as Record<string, unknown>).id === 'string'
      );
    };

    const rawNodes = agent.agent_metadata.nodes;
    if (!Array.isArray(rawNodes)) {
      console.warn('agent_metadata.nodes is not an array, using empty nodes');
      return { nodes: [] };
    }

    // Validate and sanitize metadata nodes
    const validatedNodes = rawNodes.filter(isValidMetadataNode).map(node => ({
      id: node.id,
      position: node.position || { x: 0, y: 0 },
      measured: node.measured,
    }));

    if (validatedNodes.length !== rawNodes.length) {
      console.warn(
        `Filtered out ${rawNodes.length - validatedNodes.length} invalid metadata nodes`
      );
    }

    return {
      nodes: validatedNodes,
    };
  }, [agent?.agent_metadata]);

  // Convert workflow data to graph format using the hook
  const { nodes, edges } = useWorkflowToGraph(
    agent?.steps || {},
    agent?.triggers || [],
    agent?.start_step_id || '',
    metadata
  );

  const agentWithGraph = useMemo(() => {
    if (!agent) return undefined;

    return {
      ...agent,
      nodes,
      edges,
    };
  }, [agent, nodes, edges]);

  return { agent: agentWithGraph, isPending, error };
}

export function useCreateWorkflowAgent(onSuccess?: (agent: WorkflowAgent) => void) {
  const queryClient = useQueryClient();
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (agent: WorkflowAgent) => {
      return createWorkflowAgent(agent, instanceId ?? '');
    },
    onSuccess: savedAgent => {
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });
      toaster.create('Workflow agent created successfully', { intent: 'success' });
      onSuccess?.(savedAgent);
    },
    onError: () => {
      toaster.create('Failed to save workflow agent', { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useUpdateWorkflowAgent(onSuccess?: (agent: WorkflowAgent) => void) {
  const queryClient = useQueryClient();
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      updatedAgent,
      updateUrl,
    }: {
      updatedAgent: WorkflowAgent;
      updateUrl: string;
    }) => {
      return updateWorkflowAgent(updatedAgent, updateUrl);
    },

    onSuccess: updatedAgent => {
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });
      queryClient.invalidateQueries({
        queryKey: [
          'opal:workflow-agent',
          `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${updatedAgent.id}`,
        ],
      });
      toaster.create('Workflow agent updated successfully', { intent: 'success' });
      onSuccess?.(updatedAgent);
    },
    onError: () => {
      toaster.create('Failed to update workflow agent', { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useDeleteWorkflowAgent(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (deleteUrl: string) => {
      return deleteWorkflowAgent(deleteUrl);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opal:workflow-agents'] });
      toaster.create('Workflow agent deleted successfully', { intent: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to delete workflow agent', { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useTriggerWorkflowAgent(onSuccess?: (data: WorkflowAgentTriggerResponse) => void) {
  const { instanceId } = useInstanceId();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ agentId, inputText }: { agentId: string; inputText: string }) => {
      return triggerWorkflowAgent(instanceId ?? '', agentId, inputText);
    },
    onSuccess: data => {
      toaster.create('Workflow agent triggered successfully', { intent: 'success' });
      onSuccess?.(data);
    },
    onError: () => {
      toaster.create('Failed to trigger workflow agent', { intent: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useGetWorkflowAgentExecution(executionId: string | undefined) {
  const { instanceId } = useInstanceId();
  const { data, isFetching, error } = useQuery<WorkflowAgentExecution>({
    queryKey: ['opal:workflow-agent-execution', executionId],
    queryFn: () => getWorkflowAgentExecution(instanceId ?? '', executionId || ''),
    enabled: !!executionId && !!instanceId,
    refetchInterval: (query: Query<WorkflowAgentExecution, Error>) => {
      return query.state?.data?.status === 'pending' ? 3000 : false;
    },
  });
  return { data, isFetching, error };
}
